import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AppHeader from "@/components/app/AppHeader";
import OtterIcon from "@/components/app/OtterIcon";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ArrowRight, Settings2, FileText, ExternalLink,
  TrendingUp, TrendingDown, Minus, AlertCircle, CheckCircle2, Loader2,
  RefreshCw, Wand2,
} from "lucide-react";
import InlineFixDrawer from "@/components/app/InlineFixDrawer";
import PlanLockedCard from "@/components/app/PlanLockedCard";

const SCORE_TONE = {
  green: "border-emerald-500 text-emerald-600 bg-emerald-50",
  amber: "border-amber-500 text-amber-600 bg-amber-50",
  red:   "border-red-500 text-red-600 bg-red-50",
  none:  "border-border text-muted-foreground bg-secondary/30",
};

function scoreColorClass(score) {
  if (score == null) return SCORE_TONE.none;
  if (score >= 80) return SCORE_TONE.green;
  if (score >= 50) return SCORE_TONE.amber;
  return SCORE_TONE.red;
}

function scoreLabel(score) {
  if (score == null) return "No audit yet";
  if (score >= 90) return "Excellent";
  if (score >= 80) return "Healthy";
  if (score >= 70) return "Good";
  if (score >= 50) return "Needs work";
  return "Critical";
}

const IMPACT_BADGE = {
  high:   "border-red-500 text-red-600 bg-red-50",
  medium: "border-amber-500 text-amber-700 bg-amber-50",
  low:    "border-border text-muted-foreground bg-secondary/40",
};

const IMPACT_LABEL = {
  high: "do first",
  medium: "this week",
  low: "nice to have",
};

/**
 * Simple Mode dashboard — for SMB users (default after Quick Start).
 *
 * Shows ONE project (their first one), with:
 *   • Big score (color-coded)
 *   • 3 plain-English next actions, ranked by impact
 *   • A "wins" tally to keep them feeling progress
 *   • Buttons: "Run new audit", "Ask Arlo to draft a post", "Open project"
 *   • Toggle at the bottom to switch to advanced/pro mode.
 */
export default function SimpleDashboard({ user, onSwitchToPro }) {
  const navigate = useNavigate();

  const [project, setProject] = useState(null);   // first project or null
  const [actions, setActions] = useState(null);   // {score, actions, audited_at, ...}
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [polling, setPolling] = useState(false);
  const [fixAction, setFixAction] = useState(null); // action currently shown in InlineFixDrawer

  const load = async () => {
    setLoading(true);
    try {
      const { data: projects } = await api.get("/projects");
      const p = (projects || [])[0] || null;
      setProject(p);
      if (p) {
        const { data } = await api.get(`/projects/${p.id}/next-actions`);
        setActions(data);
      } else {
        setActions(null);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Quick-start auto-audit polling. If the user has a project but no audit
  // yet (likely just came through /quick-start which queues an audit in the
  // background), poll every 3s for up to 30s. Once the audit lands, refresh
  // the actions automatically — user never has to click anything.
  useEffect(() => {
    if (!project || !actions) return;
    if (!actions.no_audit_yet) return;
    setPolling(true);
    let ticks = 0;
    const maxTicks = 10; // 30s total
    const handle = setInterval(async () => {
      ticks += 1;
      try {
        const { data } = await api.get(`/projects/${project.id}/next-actions`);
        if (!data.no_audit_yet) {
          setActions(data);
          setPolling(false);
          clearInterval(handle);
          toast.success(`Audit complete — score ${data.score}/100. ${data.actions?.length ? `Arlo flagged ${data.actions.length} thing${data.actions.length === 1 ? "" : "s"} to fix.` : "You're in great shape."}`);
          return;
        }
      } catch { /* silent — keep polling */ }
      if (ticks >= maxTicks) {
        setPolling(false);
        clearInterval(handle);
      }
    }, 3000);
    return () => clearInterval(handle);
  }, [project, actions]);

  async function runAudit() {
    if (!project) return;
    setRunning(true);
    try {
      await api.post(`/projects/${project.id}/audit`);
      toast.success("Audit complete — refreshing your actions.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  }

  // No project → push them to Quick Start.
  if (!loading && !project) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="simple-dashboard-empty">
          <div className="w-14 h-14 mx-auto bg-foreground grid place-items-center text-background mb-4">
            <OtterIcon size={36} />
          </div>
          <h1 className="font-semibold tracking-tight text-3xl">Let&apos;s get you set up.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Arlo needs to know what your business does. Takes 90 seconds.
          </p>
          <div className="mt-6">
            <Button asChild size="lg" className="rounded-none bg-foreground hover:bg-foreground/90">
              <Link to="/quick-start" data-testid="simple-dashboard-go-quickstart">
                <Sparkles className="w-4 h-4 mr-2" /> Start setup →
              </Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const score = actions?.score;
  const wins = actions?.wins ?? 0;
  const noAuditYet = actions?.no_audit_yet;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="simple-dashboard">
        {/* Hello */}
        <div className="border-b border-border pb-8">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
            // your SEO snapshot · {project?.domain}
          </div>
          <h1 className="font-semibold tracking-tight mt-2 text-3xl sm:text-4xl leading-tight">
            Hey{user?.name ? `, ${user.name.split(" ")[0]}` : ""} — here&apos;s where you stand.
          </h1>
        </div>

        <div className="mt-8">
          <PlanLockedCard user={user} />
        </div>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your snapshot…
          </div>
        ) : (
          <>
            {/* Score card */}
            <section className="mt-10 grid sm:grid-cols-[auto,1fr] gap-6 items-center border border-foreground bg-white p-6" data-testid="simple-dashboard-score-card">
              <div className={`w-32 h-32 border-2 grid place-items-center flex-shrink-0 ${scoreColorClass(score)}`}>
                <div className="text-center">
                  <div className="text-4xl font-semibold tracking-tight leading-none">
                    {score == null ? "—" : score}
                  </div>
                  <div className="font-mono-data text-[10px] uppercase tracking-widest mt-1.5 opacity-80">
                    /100
                  </div>
                </div>
              </div>
              <div>
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                  // your score
                </div>
                <div className="font-semibold tracking-tight text-2xl mt-1">{scoreLabel(score)}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  {score == null
                    ? "We haven't audited your site yet. Tap below to run your first one — takes about 10 seconds."
                    : score >= 80
                      ? `Nice work. You're already in good shape with ${wins} wins on this page.`
                      : score >= 50
                        ? `Solid foundation — there are a few quick fixes below that'll push you higher.`
                        : `A few important things to fix. Don't worry — Arlo's going to walk you through each.`}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={runAudit}
                    disabled={running}
                    className="rounded-none bg-foreground hover:bg-foreground/90"
                    data-testid="simple-dashboard-run-audit"
                  >
                    {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    Re-run audit
                  </Button>
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                    data-testid="simple-dashboard-open-project"
                  >
                    <Link to={`/app/projects/${project.id}`}>
                      Open full project <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </div>
            </section>

            {/* Next 3 actions */}
            <section className="mt-10" data-testid="simple-dashboard-actions">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h2 className="font-semibold tracking-tight text-xl">Do these {Math.min(3, actions?.actions?.length || 0) || 3} things this week.</h2>
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">// ranked by impact</span>
              </div>

              {/* Projection banner — only when we have actions AND a real lift */}
              {actions?.actions?.length > 0 && actions.projected_score_if_all_fixed > (actions.score || 0) && (
                <div
                  className="mb-4 border border-emerald-500 bg-emerald-50 text-emerald-900 px-4 py-3 flex items-center gap-3"
                  data-testid="simple-dashboard-projection"
                >
                  <TrendingUp className="w-4 h-4 flex-shrink-0 text-emerald-600" />
                  <div className="text-sm">
                    Fix all {actions.actions.length} below and Arlo projects you&apos;ll hit{" "}
                    <span className="font-semibold tracking-tight">
                      {actions.projected_score_if_all_fixed}/100
                    </span>
                    {" "}<span className="font-mono-data text-xs opacity-70">
                      (+{actions.projected_score_if_all_fixed - (actions.score || 0)} pts from where you are)
                    </span>
                  </div>
                </div>
              )}

              {noAuditYet ? (
                polling ? (
                  <div className="border border-foreground bg-foreground text-background p-6 flex items-center gap-4 relative overflow-hidden" data-testid="simple-dashboard-auditing">
                    <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
                    <div className="relative w-10 h-10 bg-background text-foreground grid place-items-center flex-shrink-0">
                      <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                    <div className="relative">
                      <div className="font-semibold tracking-tight text-lg leading-tight">Arlo is auditing your site…</div>
                      <p className="text-sm text-background/70 mt-1">
                        Takes about 10 seconds. Your score and action list will appear here as soon as it&apos;s done — no need to refresh.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed border-foreground/30 p-8 text-center text-sm text-muted-foreground" data-testid="simple-dashboard-no-audit">
                    Run your first audit (button above) — Arlo will show you exactly what to fix.
                  </div>
                )
              ) : (actions?.actions?.length || 0) === 0 ? (
                score != null && score < 50 ? (
                  <div className="border border-foreground bg-red-50 p-6 flex items-start gap-4" data-testid="simple-dashboard-low-score-no-actions">
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    <div>
                      <div className="font-semibold tracking-tight text-lg leading-snug">We couldn&apos;t pinpoint what&apos;s wrong.</div>
                      <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Your score is low but Arlo didn&apos;t recognize the specific issues — usually means the audit couldn&apos;t reach the page (down site, firewall, or login required). Double-check the URL is publicly accessible and re-run the audit.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="border border-foreground bg-foreground text-background p-6 relative overflow-hidden" data-testid="simple-dashboard-all-good">
                    <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
                    <div className="relative flex items-start gap-4">
                      <CheckCircle2 className="w-8 h-8 flex-shrink-0" />
                      <div>
                        <div className="font-semibold tracking-tight text-xl">Inbox zero on the basics.</div>
                        <p className="text-sm text-background/70 mt-1">
                          Your audit didn&apos;t flag any high-impact issues. Time to ship some content — let Arlo draft your next post.
                        </p>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <ol className="space-y-3" data-testid="simple-dashboard-actions-list">
                  {actions.actions.map((a, idx) => (
                    <li
                      key={idx}
                      className="border border-border bg-white p-5 flex items-start gap-4"
                      data-testid={`simple-dashboard-action-${idx}`}
                    >
                      <div className="font-mono-data text-xs text-muted-foreground w-6 flex-shrink-0 pt-0.5">
                        #{idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <h3 className="font-semibold tracking-tight text-base leading-snug">{a.title}</h3>
                          <span
                            className={`font-mono-data text-[10px] uppercase tracking-widest border px-1.5 py-0.5 ${IMPACT_BADGE[a.impact] || IMPACT_BADGE.medium}`}
                            data-testid={`simple-dashboard-action-${idx}-impact`}
                          >
                            {IMPACT_LABEL[a.impact] || a.impact}
                          </span>
                          {a.score_lift > 0 && (
                            <span
                              className="font-mono-data text-[10px] uppercase tracking-widest border border-emerald-500 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 inline-flex items-center gap-1"
                              data-testid={`simple-dashboard-action-${idx}-lift`}
                            >
                              <TrendingUp className="w-2.5 h-2.5" /> +{a.score_lift} pts
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{a.description}</p>
                        {a.has_inline_fix && (
                          <Button
                            size="sm"
                            onClick={() => setFixAction(a)}
                            className="mt-3 rounded-none bg-foreground hover:bg-foreground/90"
                            data-testid={`simple-dashboard-action-${idx}-fix-it`}
                          >
                            <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                            Let Arlo write this for me
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <InlineFixDrawer
              projectId={project?.id}
              action={fixAction}
              currentScore={actions?.score}
              open={!!fixAction}
              onOpenChange={(o) => { if (!o) setFixAction(null); }}
              onFixApplied={() => { setFixAction(null); load(); }}
            />

            {/* Ask Arlo / content CTA */}
            <section className="mt-10 border border-foreground bg-foreground text-background p-6 relative overflow-hidden" data-testid="simple-dashboard-content-cta">
              <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
              <div className="relative grid sm:grid-cols-[1fr,auto] gap-4 items-center">
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">
                    // need to publish more?
                  </div>
                  <div className="font-semibold tracking-tight text-xl mt-1.5 leading-tight">
                    Let Arlo draft your next blog post.
                  </div>
                  <p className="text-sm text-background/70 mt-1.5 max-w-xl">
                    Pick a keyword, Arlo writes the full post — title, meta, body, and a publish-ready Markdown draft. Edit and ship in minutes.
                  </p>
                </div>
                <Button
                  asChild
                  className="rounded-none bg-background text-foreground hover:bg-background/90"
                  data-testid="simple-dashboard-content-cta-button"
                >
                  <Link to={`/app/projects/${project.id}?tab=content`}>
                    <FileText className="w-4 h-4 mr-2" /> Open Content Studio <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Link>
                </Button>
              </div>
            </section>

            {/* Pro toggle */}
            <section className="mt-10 border-t border-border pt-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">// more tools</div>
                <div className="text-sm mt-1">
                  Want the full dashboard with keywords, backlinks, competitors, and audits?
                </div>
              </div>
              <Button
                variant="outline"
                className="rounded-none"
                onClick={onSwitchToPro}
                data-testid="simple-dashboard-switch-to-pro"
              >
                <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Switch to advanced
              </Button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
